// The Walk Set adds two real variants. Shopify applies the qualifying pair discount.
if (!customElements.get('b2c-walk-builder')) {
  customElements.define('b2c-walk-builder', class extends HTMLElement {
    connectedCallback() {
      this.catalog = JSON.parse(this.querySelector('[data-builder-catalog]').textContent);
      this.root = this.closest('.b2c-product');
      this.picker = this.root.querySelector('kit-variant-picker');
      this.buy = this.root.querySelector('block-buy-buttons');
      if (!this.picker || !this.buy || !this.catalog.selected) return;
      this.fr = this.dataset.lang === 'fr';
      this.de = this.dataset.lang === 'de';
      const selected = this.catalog.selected;
      this.state = {collar: {colour: selected.option1, size: selected.option2}, leash: {colour: selected.option1, size: selected.option3}};
      // 17 Oct 2026 (Luuk): only the Standard leash is sold on the webshop.
      if (this.catalog.leash.some(v => v.size === 'Standard')) this.state.leash.size = 'Standard';
      this.controller = new AbortController();
      this.pending = false;
      // The neck size shown first is not treated as chosen until the visitor picks one,
      // unless the page was opened on a specific set variant. Campaign links add size=choose:
      // they keep the colour from the variant but still ask for the neck size.
      const query = new URLSearchParams(location.search);
      this.neckChosen = query.has('variant') && query.get('size') !== 'choose';
      this.querySelector('[data-component-form]').addEventListener('submit', event => {
        event.preventDefault();
        this.addComponents();
      }, {signal: this.controller.signal});
      // A campaign link (size=choose) opens on the chosen colourway instead of the default lifestyle photo.
      this.galleryView = query.get('size') === 'choose' ? 'set' : 'photos';
      this.galleryThumb = this.root.querySelector('.b2c-current-set-thumb');
      if (this.galleryThumb) this.root.querySelector('[data-product-thumbs]')?.prepend(this.galleryThumb);
      const choosePhoto = event => {
        const thumb = event.target.closest('[data-product-thumb], .product__thumb-arrow, .b2c-current-set-thumb');
        if (!thumb) return;
        if (event.type === 'focusin' && thumb === this.galleryThumb) return;
        this.galleryView = thumb === this.galleryThumb ? 'set' : 'photos';
        this.updateGallery();
      };
      this.root.addEventListener('click', choosePhoto, {capture: true, signal: this.controller.signal});
      this.root.addEventListener('focusin', choosePhoto, {signal: this.controller.signal});
      this.addEventListener('change', event => {
        if (this.pending) return;
        const control = event.target;
        if (control.matches('[data-match-colours]')) {
          if (control.checked) this.state.leash.colour = this.state.collar.colour;
        } else if (control.matches('[data-choice]')) {
          const piece = control.closest('[data-piece]').dataset.piece;
          this.state[piece][control.dataset.choice] = control.value;
          if (piece === 'collar' && control.dataset.choice === 'size') this.neckChosen = true;
          if (piece === 'collar' && control.dataset.choice === 'colour' && this.querySelector('[data-match-colours]').checked) {
            this.state.leash.colour = control.value;
          }
          if (piece === 'leash' && control.dataset.choice === 'colour') {
            if (!this.matching()) this.querySelector('[data-match-colours]').checked = false;
          }
        } else return;
        this.querySelector('[data-builder-error]').hidden = true;
        this.galleryView = 'set';
        this.render();
        this.syncSet();
      }, {signal: this.controller.signal});
      this.buy.querySelector('form')?.addEventListener('submit', event => {
        // Capture protects against keyboard submission and a stale native response.
        if (this.componentPurchase() || !this.canOrder() || !this.neckChosen) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.componentPurchase()) this.addComponents();
          else if (!this.neckChosen) this.askNeckSize();
        }
      }, {capture: true, signal: this.controller.signal});
      this.observer = new MutationObserver(() => {
        if (this.componentPurchase() || !this.canOrder()) this.lockPurchase();
      });
      this.observer.observe(this.buy, {attributes: true, childList: true, subtree: true, attributeFilter: ['disabled']});
      this.render();
      this.hidden = false;
      this.root.dataset.b2cBuilderReady = 'true';
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
    matching() { return this.state.collar.colour === this.state.leash.colour; }
    component(piece, size = this.state[piece].size) {
      return this.catalog[piece].find(v => v.colour === this.state[piece].colour && v.size === size);
    }
    canOrder() { return !!(this.component('collar')?.available && this.component('leash')?.available); }
    componentPurchase() { return true; }
    saving(leashPrice) {
      const discount = this.catalog.discount;
      if (!discount?.enabled) return 0;
      return Math.min(leashPrice, discount.type === 'amount' ? discount.value : Math.floor(leashPrice * discount.value));
    }
    money(cents) {
      // Follow the storefront's own money format, read from a sample rendered by Liquid.
      const sample = this.catalog.moneySample;
      const pattern = sample?.match(/1(\D?)234(\D)56/);
      if (pattern) {
        const [whole, fraction] = (cents / 100).toFixed(2).split('.');
        return sample.replace(pattern[0], whole.replace(/\B(?=(\d{3})+(?!\d))/g, pattern[1]) + pattern[2] + fraction);
      }
      return new Intl.NumberFormat(this.fr ? 'fr-FR' : this.de ? 'de-DE' : 'en-IE', {style: 'currency', currency: this.catalog.currency}).format(cents / 100);
    }
    lockPurchase() {
      const button = this.buy.querySelector('[name="add"]');
      if (button && !button.disabled) button.disabled = true;
      const stock = this.buy.querySelector('[data-astra-dispatch-status]');
      if (stock && !stock.hidden) stock.hidden = true;
    }
    updateGallery() {
      const mixed = !this.matching();
      const preview = this.galleryView === 'set';
      this.root.dataset.b2cGalleryView = preview ? 'set' : 'photos';
      const mixedPicture = this.root.querySelector('.b2c-mixed-picture');
      if (mixedPicture) mixedPicture.hidden = !preview;
      if (this.galleryThumb) {
        this.galleryThumb.hidden = false;
        this.galleryThumb.setAttribute('aria-pressed', String(preview));
      }
      if (preview) this.root.querySelector('.product__thumbs--scroller').scrollLeft = 0;
      // Re-measure the native carousel after revealing it from the set preview.
      if (!preview) this.root.querySelector('product-images')?.flickity?.resize();
    }
    render() {
      const words = [];
      for (const piece of ['collar', 'leash']) {
        const card = this.querySelector(`[data-piece="${piece}"]`);
        const state = this.state[piece];
        card.querySelectorAll('[data-choice]').forEach(input => { input.checked = input.value === state[input.dataset.choice] && (this.neckChosen || piece !== 'collar' || input.dataset.choice !== 'size'); });
        card.querySelector('[data-piece-colour]').textContent = state.colour;
        const sizeName = card.querySelector('[data-piece-size-name]');
        if (sizeName) { const picked = card.querySelector('[data-choice="size"]:checked'); sizeName.textContent = picked ? `: ${picked.closest('label').querySelector('.b2c-piece-size-name').textContent.trim()}` : ''; }
        const variant = this.component(piece);
        const photo = variant || this.catalog[piece].find(v => v.colour === state.colour);
        if (photo?.image) {
          card.querySelector('[data-piece-image]').src = photo.image;
          card.querySelector('[data-piece-image]').alt = `${this.fr ? (piece === 'collar' ? 'Collier' : 'Laisse') : this.de ? (piece === 'collar' ? 'Halsband' : 'Leine') : piece} — ${state.colour}`;
          const galleryPhoto = this.root.querySelector(`[data-gallery-piece="${piece}"]`);
          if (galleryPhoto) { galleryPhoto.src = photo.image.replace(/width=\d+/, 'width=800'); galleryPhoto.alt = card.querySelector('[data-piece-image]').alt; }
          const thumbPhoto = this.galleryThumb?.querySelector(`[data-gallery-thumb-piece="${piece}"]`);
          if (thumbPhoto) thumbPhoto.src = photo.image;
        }
        const stock = card.querySelector('[data-piece-stock]');
        stock.textContent = variant?.available ? (this.fr ? 'En stock' : this.de ? 'Auf Lager' : 'In stock') : (this.fr ? 'Indisponible dans cette taille' : this.de ? 'In dieser Größe nicht verfügbar' : 'Unavailable in this size');
        stock.dataset.available = String(!!variant?.available);
        card.querySelectorAll('.b2c-piece-size').forEach(label => {
          const input = label.querySelector('input');
          const available = this.component(piece, input.value)?.available;
          label.dataset.unavailable = String(!available);
        });
        const size = this.fr ? ({Small:'Petit', Medium:'Moyen', Large:'Grand'}[state.size] || state.size) : this.de ? ({Small:'Klein', Medium:'Mittel', Large:'Groß'}[state.size] || state.size) : state.size;
        const sizeWord = piece === 'collar' && !this.neckChosen ? (this.fr ? 'taille à choisir' : this.de ? 'Größe wählen' : 'size to choose') : size;
        words.push(`${piece === 'collar' ? (this.fr ? 'Collier' : this.de ? 'Halsband' : 'Collar') : (this.fr ? 'Laisse' : this.de ? 'Leine' : 'Leash')}: ${state.colour} / ${sizeWord}`);
      }
      const widthHelp = this.querySelector('[data-leash-width-help]');
      const smallLeash = ['Small', 'Petit'].includes(this.state.leash.size);
      widthHelp.hidden = !smallLeash;
      widthHelp.textContent = smallLeash ? (this.fr ? 'La sangle plus fine convient aux petits chiens.' : this.de ? 'Das schmalere Band passt zu kleineren Hunden.' : 'A narrower strap for smaller dogs.') : '';
      this.querySelector('[data-set-summary]').textContent = words.join('\n');
      const mixed = !this.matching();
      this.root.dataset.b2cMixed = String(mixed);
      const mixedPicture = this.root.querySelector('.b2c-mixed-picture');
      if (mixedPicture) mixedPicture.querySelector('[data-gallery-summary]').textContent = words.join(' · ');
      this.updateGallery();
      const componentPurchase = this.componentPurchase();
      this.root.dataset.b2cComponents = String(componentPurchase);
      const purchase = this.querySelector('[data-component-form]');
      purchase.hidden = !componentPurchase;
      const separateTotal = (this.component('collar')?.price || 0) + (this.component('leash')?.price || 0);
      const saving = this.saving(this.component('leash')?.price || 0);
      const total = separateTotal - saving;
      this.total = total;
      this.separateTotal = separateTotal;
      {
        const headingSaving = this.root.querySelector('[data-heading-saving]');
        if (headingSaving) {
          headingSaving.hidden = saving <= 0;
          headingSaving.textContent = (this.fr ? 'Économisez ' : this.de ? 'Spare ' : 'Save ') + this.money(saving);
          // 17 Oct 2026: the heading also shows the separate price struck through.
          const headingSeparate = this.root.querySelector('[data-heading-separate]');
          if (headingSeparate) { headingSeparate.hidden = saving <= 0; headingSeparate.textContent = this.money(separateTotal); }
        }
        // The saving itself sits in the badge on the set card (round 7), so the note no longer repeats it.
        this.querySelector('[data-component-price-note]').textContent = this.fr ? 'Vos couleurs. Le même prix de set.' : this.de ? 'Deine Farben. Derselbe Set-Preis.' : 'Any colours. The same set price.';
      }
      this.querySelector('[data-component-total]').textContent = this.money(total);
      // Round 7: separate price struck through and the saving badge on the set card, the price in the mobile bar,
      // and the shipping text of the trust line (free once the set reaches the threshold). All optional in the markup.
      const separateOut = this.querySelector('[data-component-separate]');
      if (separateOut) { separateOut.hidden = saving <= 0; separateOut.textContent = this.money(separateTotal); }
      const savingBadge = this.querySelector('[data-set-saving]');
      if (savingBadge) { savingBadge.hidden = saving <= 0; savingBadge.textContent = (this.fr ? 'Économisez ' : this.de ? 'Spare ' : 'Save ') + this.money(saving); }
      const stickyPrice = this.root.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = this.money(total);
      const shipping = this.root.querySelector('[data-trust-shipping]');
      const threshold = Number(this.root.querySelector('[data-trust-line]')?.dataset.threshold || 0);
      if (shipping && threshold > 0) shipping.textContent = total >= threshold ? shipping.dataset.textFree : shipping.dataset.textFrom;
      const headingPrice = this.root.querySelector('[data-component-heading]');
      headingPrice.hidden = !componentPurchase;
      headingPrice.querySelector('[data-component-price]').textContent = this.money(total);
      this.querySelector('[data-component-stock]').hidden = !this.canOrder();
      this.updatePurchaseButton();
      const notice = this.querySelector('[data-builder-notice]');
      notice.hidden = this.canOrder();
      notice.textContent = this.fr ? 'Un article est indisponible dans cette taille. Choisissez une autre taille ou couleur.' : this.de ? 'Ein Artikel ist in dieser Größe nicht verfügbar. Wähle eine andere Größe oder Farbe.' : 'An item is unavailable in this size. Choose another size or colour.';
      if (componentPurchase || !this.canOrder()) this.lockPurchase();
    }
    updatePurchaseButton() {
      const button = this.querySelector('[data-component-add]');
      button.disabled = this.pending || !this.canOrder();
      button.setAttribute('aria-busy', String(this.pending));
      // 17 Oct 2026 (Luuk): no "Choose your collar size" label; the button always reads "Add my set to cart · $85.00" and a tap without a neck size scrolls to the sizes (askNeckSize).
      // Round 7: the price follows the label once the set can be added ("Add my set to cart · $85.00"), as on the collar and leash pages.
      // 17 Oct 2026 (Luuk): the set card under the builder is gone, so the button carries the separate price struck through ("Add to cart · $95.50 $85.00", label as on the other pages).
      const label = this.pending ? (this.fr ? 'Ajout en cours…' : this.de ? 'Wird hinzugefügt…' : 'Adding…') : !this.canOrder() ? (this.fr ? 'Indisponible' : this.de ? 'Nicht verfügbar' : 'Unavailable') : (this.fr ? 'Ajouter au panier' : this.de ? 'In den Warenkorb' : 'Add to cart');
      button.replaceChildren(label);
      if (!this.pending && this.canOrder() && this.total > 0) {
        button.append(' · ');
        if (this.separateTotal > this.total) { const was = document.createElement('s'); was.className = 'b2c-mixed-atc-was'; was.textContent = this.money(this.separateTotal); button.append(was, ' '); }
        button.append(this.money(this.total));
      }
    }
    askNeckSize() {
      const field = this.querySelector('[data-piece="collar"] .b2c-piece-sizes');
      field.scrollIntoView({block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      field.classList.remove('b2c-size-attention');
      requestAnimationFrame(() => field.classList.add('b2c-size-attention'));
      field.querySelector('input')?.focus({preventScroll: true});
    }
    async addComponents() {
      if (this.pending || !this.componentPurchase() || !this.canOrder()) return;
      if (!this.neckChosen) { this.askNeckSize(); return; }
      const collar = this.component('collar'), leash = this.component('leash');
      const properties = {_walk_set: `${collar.id}:${leash.id}`};
      const items = [collar, leash].map(variant => ({id: variant.id, quantity: 1, properties}));
      const error = this.querySelector('[data-builder-error]');
      error.hidden = true;
      this.pending = true;
      this.querySelectorAll('[data-choice], [data-match-colours]').forEach(control => { control.disabled = true; });
      this.updatePurchaseButton();
      const route = window.Shopify.routes.root;
      let added = false;
      try {
        const response = await fetch(`${route}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items, sections: ['cart-ajax'], sections_url: window.location.pathname})
        });
        const cart = await response.json();
        if (!response.ok) {
          const message = typeof cart.description === 'string' ? cart.description : cart.message;
          throw new Error(message || (this.fr ? 'Vérifiez la disponibilité de vos articles.' : this.de ? 'Bitte prüfe die Verfügbarkeit deiner Artikel.' : 'Please check the availability of your items.'));
        }
        added = true;
        // Once Shopify accepts the add, a drawer/render failure must never invite
        // a duplicate add. The cart page is the fallback for missing sections.
        if (this.buy.dataset.cartType === 'page' || !cart.sections?.['cart-ajax']) {
          window.location.assign(`${route}cart`);
        } else {
          this.dispatchEvent(new CustomEvent('ajaxProduct:added', {bubbles: true, detail: {product: cart, addToCartBtn: this.querySelector('[data-component-add]')}}));
        }
      } catch (failure) {
        if (added) {
          window.location.assign(`${route}cart`);
        } else {
          error.textContent = failure instanceof TypeError || failure instanceof SyntaxError
            ? (this.fr ? 'Impossible de confirmer l’ajout. Vérifiez votre panier avant de réessayer.' : this.de ? 'Wir konnten das Hinzufügen nicht bestätigen. Prüfe deinen Warenkorb, bevor du es erneut versuchst.' : 'We could not confirm the add. Check your cart before trying again.')
            : failure.message;
          error.hidden = false;
        }
      } finally {
        this.pending = false;
        this.querySelectorAll('[data-choice], [data-match-colours]').forEach(control => { control.disabled = false; });
        this.updatePurchaseButton();
      }
    }
    syncSet() {
      if (!this.matching()) return;
      const options = [this.state.collar.colour, this.state.collar.size, this.state.leash.size];
      const fields = [...this.picker.querySelectorAll('fieldset')];
      let last;
      for (let index = 0; index < options.length; index++) {
        const input = [...(fields[index]?.querySelectorAll('input[type="radio"]') || [])].find(input => input.value === options[index]);
        if (!input) { this.lockPurchase(); return; }
        input.checked = true;
        last = input;
      }
      // One native selection request reads all three options; it updates price,
      // gallery, inventory guards and the native cart variant together.
      last?.dispatchEvent(new Event('change', {bubbles: true}));
    }
  });
}

